// DAS-3258: the Alpha card on the stock page — which of the register's proven signals this
// stock carries today, each with its measured edge, and the best single edge as the headline
// (never a sum: DAS-3255). Data: GET /alpha_card/<ticker> (DAS-3256) and GET /alpha_register
// (DAS-3157); the join and the wording live in src/functions/alphaCard.js (DAS-3257).
// DAS-3269: shows a spinner while the card is fetched, the day the stored card was computed,
// and an amber note when that is more than two days ago. Renders nothing on an API error or
// when the card has no lines.
import React, { useEffect, useState } from 'react';
import { Spin } from 'antd';
import api from '../functions/api';
import { buildAlphaCard } from '../functions/alphaCard';

// The modal body is white: antd's green / grey / amber, as the fundamentals summary line uses.
const COLOR_CARRIES = '#389e0d';
const COLOR_NOT = '#999';
const COLOR_UNKNOWN = '#d48806';
const COLOR_TEXT2 = '#666';
export const STALE_AFTER_DAYS = 2;

function mark(carries) {
  if (carries === true) return <span style={{ color: COLOR_CARRIES, fontWeight: 'bold' }} title="carries this signal today">✓</span>;
  if (carries === false) return <span style={{ color: COLOR_NOT }} title="does not carry this signal today">—</span>;
  return <span style={{ color: COLOR_UNKNOWN }} title="no data for this stock">?</span>;
}

export function registerRowsFrom(registerResponse) {
  // The register endpoint answers {usable: [...], no_alpha: [...]}; the card needs every row
  // that has a signal key, whatever its category.
  if (!registerResponse) return [];
  if (Array.isArray(registerResponse)) return registerResponse;
  return [...(registerResponse.usable || []), ...(registerResponse.no_alpha || [])];
}

export function daysOld(dateText, now = new Date()) {
  // Whole days between the card's computed date (YYYY-MM-DD) and now; null when unknown.
  if (!dateText) return null;
  const d = new Date(`${String(dateText).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((now - d) / 86400000);
}

const AlphaCardBlock = ({ ticker }) => {
  const [card, setCard] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setCard(null);
    setMeta(null);
    if (!ticker) return undefined;
    setLoading(true);
    const load = async () => {
      try {
        const cardRes = await api.get(`/alpha_card/${encodeURIComponent(ticker)}`);
        let registerRows = [];
        try {
          const regRes = await api.get('/alpha_register');
          registerRows = registerRowsFrom(regRes && regRes.data);
        } catch (e) {
          registerRows = []; // the card entries carry the register figures themselves
        }
        if (!cancelled) {
          const data = (cardRes && cardRes.data) || {};
          setCard(buildAlphaCard(data, registerRows));
          setMeta({ source: data.source || null, computedDate: data.computed_date || data.as_of || null });
        }
      } catch (e) {
        if (!cancelled) setCard(null); // no broken block
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [ticker]);

  if (loading) {
    return (
      <div data-testid="alpha-card-loading" style={{ fontSize: '12px', color: COLOR_TEXT2, margin: '10px 0' }}>
        <Spin size="small" /> <span style={{ marginLeft: '6px' }}>Alpha card loading</span>
      </div>
    );
  }
  if (!card || !card.lines || card.lines.length === 0) return null;

  const positive = card.headline.startsWith('Expected edge');
  const age = daysOld(meta && meta.computedDate);
  const stale = age != null && age > STALE_AFTER_DAYS;
  return (
    <div data-testid="alpha-card" style={{ fontSize: '12px', marginTop: '10px', marginBottom: '10px' }}>
      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
        Alpha card
        <span style={{ fontWeight: 'normal', color: COLOR_TEXT2, marginLeft: '6px' }}>
          which proven signals this stock carries today
        </span>
      </div>
      <div
        data-testid="alpha-card-headline"
        style={{ color: positive ? COLOR_CARRIES : COLOR_TEXT2, marginBottom: '2px' }}
      >
        {card.headline}
      </div>
      {meta && meta.computedDate && (
        <div data-testid="alpha-card-computed" style={{ color: stale ? COLOR_UNKNOWN : '#999', fontSize: '11px', marginBottom: '6px' }}>
          computed {String(meta.computedDate).slice(0, 10)}
          {meta.source === 'live' ? ' (live, not stored yet)' : ''}
          {stale ? ` — ${age} days old, the nightly update has not run` : ''}
        </div>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          {card.lines.map((line) => (
            <tr key={line.key} data-testid="alpha-card-row" style={{ borderTop: '1px solid #e8e8e8' }}>
              <td style={{ padding: '3px 4px', width: '18px', textAlign: 'center' }}>{mark(line.carries)}</td>
              <td style={{ padding: '3px 4px', verticalAlign: 'top' }}>
                <div>{line.label}</div>
                <div style={{ color: COLOR_TEXT2 }}>{line.valueText}</div>
                {line.note && <div style={{ color: COLOR_UNKNOWN }}>{line.note}</div>}
              </td>
              <td style={{ padding: '3px 4px', textAlign: 'right', verticalAlign: 'top', color: COLOR_TEXT2 }}>
                {line.edgeText}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AlphaCardBlock;
